import React, { useEffect, useMemo, useState } from "react";
import {
    CheckCircle2,
    Circle,
    Download,
    KeyRound,
    LogOut,
    Mail,
    Phone,
    RefreshCw,
    Search,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "scitus_admin_token";

const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const toCsv = (rows) => {
    if (!rows.length) return "";
    const headers = [
        "id",
        "created_at",
        "name",
        "company",
        "email",
        "phone",
        "industry",
        "products",
        "message",
        "email_sent",
        "confirmation_sent",
        "contacted",
    ];
    const esc = (val) => {
        if (val === null || val === undefined) return "";
        const s = Array.isArray(val) ? val.join("; ") : String(val);
        if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
        return s;
    };
    const lines = [headers.join(",")];
    for (const r of rows) {
        lines.push(headers.map((h) => esc(r[h])).join(","));
    }
    return lines.join("\n");
};

const AdminLeadsPage = () => {
    const [token, setToken] = useState(() => {
        if (typeof window === "undefined") return "";
        return window.sessionStorage.getItem(TOKEN_KEY) || "";
    });
    const [tokenInput, setTokenInput] = useState("");
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const [industry, setIndustry] = useState("");
    const [showOnly, setShowOnly] = useState("all"); // "all" | "uncontacted" | "contacted"

    useEffect(() => {
        // SEO: ensure this page is noindexed (it's admin-only)
        document.title = "Admin · Leads — Scitus.ai";
        let robots = document.querySelector('meta[name="robots"]');
        if (!robots) {
            robots = document.createElement("meta");
            robots.setAttribute("name", "robots");
            document.head.appendChild(robots);
        }
        robots.setAttribute("content", "noindex, nofollow");
        return () => {
            if (robots) robots.setAttribute("content", "index, follow");
        };
    }, []);

    const fetchLeads = async (overrideToken) => {
        const useToken = overrideToken !== undefined ? overrideToken : token;
        if (!useToken) return;
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API}/leads?limit=500`, {
                headers: { Authorization: `Bearer ${useToken}` },
            });
            if (res.status === 401 || res.status === 503) {
                setError(
                    res.status === 503
                        ? "Admin access is disabled on the server (ADMIN_API_TOKEN is not configured)."
                        : "Invalid admin token."
                );
                setLeads([]);
                window.sessionStorage.removeItem(TOKEN_KEY);
                setToken("");
                return;
            }
            if (!res.ok) {
                setError(`Server returned ${res.status}.`);
                return;
            }
            const data = await res.json();
            setLeads(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err.message || "Network error.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) fetchLeads();
    }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleLogin = (e) => {
        e.preventDefault();
        const t = tokenInput.trim();
        if (!t) return;
        window.sessionStorage.setItem(TOKEN_KEY, t);
        setToken(t);
        setTokenInput("");
    };

    const handleLogout = () => {
        window.sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
        setLeads([]);
    };

    const toggleContacted = async (lead) => {
        const next = !lead.contacted;
        // Optimistic update
        setLeads((prev) =>
            prev.map((l) => (l.id === lead.id ? { ...l, contacted: next } : l))
        );
        try {
            const res = await fetch(`${API}/leads/${lead.id}`, {
                method: "PATCH",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ contacted: next }),
            });
            if (!res.ok) throw new Error("Update failed");
        } catch (err) {
            // Revert
            setLeads((prev) =>
                prev.map((l) => (l.id === lead.id ? { ...l, contacted: !next } : l))
            );
            setError(err.message || "Could not update lead.");
        }
    };

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return leads.filter((l) => {
            if (industry && l.industry !== industry) return false;
            if (showOnly === "uncontacted" && l.contacted) return false;
            if (showOnly === "contacted" && !l.contacted) return false;
            if (!q) return true;
            return [l.name, l.company, l.email, l.message, (l.products || []).join(" ")]
                .filter(Boolean)
                .some((field) => field.toLowerCase().includes(q));
        });
    }, [leads, search, industry, showOnly]);

    const industries = useMemo(() => {
        const set = new Set();
        leads.forEach((l) => l.industry && set.add(l.industry));
        return Array.from(set).sort();
    }, [leads]);

    const stats = useMemo(() => {
        const total = leads.length;
        const contacted = leads.filter((l) => l.contacted).length;
        const last24h = leads.filter((l) => {
            if (!l.created_at) return false;
            return Date.now() - new Date(l.created_at).getTime() < 24 * 3600 * 1000;
        }).length;
        return { total, contacted, uncontacted: total - contacted, last24h };
    }, [leads]);

    const handleExport = () => {
        const csv = toCsv(filtered.length ? filtered : leads);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `scitus-leads-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // Token login view
    if (!token) {
        return (
            <section className="section" data-testid="admin-leads-login">
                <div className="container" style={{ maxWidth: 480 }}>
                    <div
                        className="card"
                        style={{ padding: 36, marginTop: 40 }}
                    >
                        <div style={{ textAlign: "center", marginBottom: 24 }}>
                            <KeyRound
                                size={36}
                                style={{ color: "var(--accent)" }}
                            />
                            <h1 className="h2" style={{ marginTop: 16, marginBottom: 6 }}>
                                Admin · Leads
                            </h1>
                            <p className="muted" style={{ fontSize: "0.92rem" }}>
                                Enter your admin token to view demo requests.
                            </p>
                        </div>
                        <form onSubmit={handleLogin} className="field-group">
                            <div className="field">
                                <label htmlFor="admin-token">Admin token</label>
                                <input
                                    id="admin-token"
                                    type="password"
                                    placeholder="Paste ADMIN_API_TOKEN"
                                    value={tokenInput}
                                    onChange={(e) => setTokenInput(e.target.value)}
                                    data-testid="admin-token-input"
                                    autoFocus
                                />
                            </div>
                            <button
                                type="submit"
                                className="btn btn-primary btn-lg"
                                style={{ width: "100%" }}
                                data-testid="admin-token-submit"
                                disabled={!tokenInput.trim()}
                            >
                                Unlock dashboard
                            </button>
                            {error && (
                                <p
                                    style={{
                                        color: "#ff7676",
                                        fontSize: "0.86rem",
                                        margin: 0,
                                        textAlign: "center",
                                    }}
                                    data-testid="admin-error"
                                >
                                    {error}
                                </p>
                            )}
                        </form>
                        <p
                            className="muted"
                            style={{
                                marginTop: 18,
                                fontSize: "0.78rem",
                                textAlign: "center",
                            }}
                        >
                            Token is cached in this browser tab only. Closing the tab logs you out.
                        </p>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="section" data-testid="admin-leads-page">
                    <div className="container">
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 16,
                                flexWrap: "wrap",
                                marginBottom: 24,
                            }}
                        >
                            <div>
                                <span className="eyebrow">Internal · Not indexed</span>
                                <h1 className="h2" style={{ marginTop: 10 }}>
                                    Demo requests
                                </h1>
                            </div>
                            <div style={{ display: "flex", gap: 8 }}>
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => fetchLeads()}
                                    disabled={loading}
                                    data-testid="admin-refresh-btn"
                                >
                                    <RefreshCw
                                        size={14}
                                        className={loading ? "spin" : ""}
                                    />{" "}
                                    Refresh
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    onClick={handleExport}
                                    data-testid="admin-export-btn"
                                    disabled={!filtered.length}
                                >
                                    <Download size={14} /> Export CSV
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    onClick={handleLogout}
                                    data-testid="admin-logout-btn"
                                >
                                    <LogOut size={14} /> Logout
                                </button>
                            </div>
                        </div>

                        {/* Stats */}
                        <div className="admin-stat-grid" data-testid="admin-stats">
                            <div className="admin-stat-card">
                                <span className="admin-stat-num">{stats.total}</span>
                                <span className="admin-stat-label">Total leads</span>
                            </div>
                            <div className="admin-stat-card">
                                <span
                                    className="admin-stat-num"
                                    style={{ color: "var(--accent)" }}
                                >
                                    {stats.last24h}
                                </span>
                                <span className="admin-stat-label">Last 24 hours</span>
                            </div>
                            <div className="admin-stat-card">
                                <span
                                    className="admin-stat-num"
                                    style={{ color: "#ffaa44" }}
                                >
                                    {stats.uncontacted}
                                </span>
                                <span className="admin-stat-label">Awaiting outreach</span>
                            </div>
                            <div className="admin-stat-card">
                                <span
                                    className="admin-stat-num"
                                    style={{ color: "#54d178" }}
                                >
                                    {stats.contacted}
                                </span>
                                <span className="admin-stat-label">Contacted</span>
                            </div>
                        </div>

                        {/* Filters */}
                        <div className="admin-filters">
                            <div className="admin-search">
                                <Search size={16} />
                                <input
                                    type="text"
                                    placeholder="Search name, company, email, message..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    data-testid="admin-search-input"
                                />
                            </div>
                            <select
                                value={industry}
                                onChange={(e) => setIndustry(e.target.value)}
                                data-testid="admin-industry-select"
                            >
                                <option value="">All industries</option>
                                {industries.map((i) => (
                                    <option key={i} value={i}>
                                        {i}
                                    </option>
                                ))}
                            </select>
                            <select
                                value={showOnly}
                                onChange={(e) => setShowOnly(e.target.value)}
                                data-testid="admin-status-filter"
                            >
                                <option value="all">All statuses</option>
                                <option value="uncontacted">Awaiting outreach</option>
                                <option value="contacted">Already contacted</option>
                            </select>
                        </div>

                        {error && (
                            <div
                                className="admin-error-banner"
                                data-testid="admin-error-banner"
                            >
                                {error}
                            </div>
                        )}

                        {/* Lead list */}
                        {loading && !leads.length ? (
                            <p
                                className="muted"
                                style={{ textAlign: "center", padding: 40 }}
                            >
                                Loading…
                            </p>
                        ) : !filtered.length ? (
                            <div
                                className="card"
                                style={{ padding: 40, textAlign: "center" }}
                            >
                                <p className="muted" style={{ margin: 0 }}>
                                    {leads.length
                                        ? "No leads match the current filters."
                                        : "No demo requests yet."}
                                </p>
                            </div>
                        ) : (
                            <div
                                className="admin-lead-list"
                                data-testid="admin-lead-list"
                            >
                                {filtered.map((lead) => (
                                    <article
                                        key={lead.id}
                                        className={`admin-lead-card ${
                                            lead.contacted ? "is-contacted" : ""
                                        }`}
                                        data-testid={`admin-lead-${lead.id}`}
                                    >
                                        <div className="admin-lead-head">
                                            <div>
                                                <h3 className="admin-lead-name">
                                                    {lead.name}{" "}
                                                    <span className="admin-lead-company">
                                                        · {lead.company}
                                                    </span>
                                                </h3>
                                                <p className="admin-lead-meta">
                                                    <a href={`mailto:${lead.email}`}>
                                                        <Mail size={13} /> {lead.email}
                                                    </a>
                                                    {lead.phone && (
                                                        <span>
                                                            <Phone size={13} /> {lead.phone}
                                                        </span>
                                                    )}
                                                    <time>{fmtDate(lead.created_at)}</time>
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                className={`admin-lead-toggle ${
                                                    lead.contacted ? "on" : ""
                                                }`}
                                                onClick={() => toggleContacted(lead)}
                                                data-testid={`admin-lead-toggle-${lead.id}`}
                                                aria-pressed={lead.contacted}
                                            >
                                                {lead.contacted ? (
                                                    <>
                                                        <CheckCircle2 size={15} /> Contacted
                                                    </>
                                                ) : (
                                                    <>
                                                        <Circle size={15} /> Mark contacted
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                        <div className="admin-lead-body">
                                            {lead.industry && (
                                                <span className="admin-pill">
                                                    {lead.industry}
                                                </span>
                                            )}
                                            {(lead.products || []).map((p) => (
                                                <span
                                                    key={p}
                                                    className="admin-pill admin-pill--product"
                                                >
                                                    {p}
                                                </span>
                                            ))}
                                        </div>
                                        {lead.message && (
                                            <p className="admin-lead-message">{lead.message}</p>
                                        )}
                                    </article>
                                ))}
                            </div>
                        )}

                        <p
                            className="muted"
                            style={{
                                marginTop: 24,
                                fontSize: "0.82rem",
                                textAlign: "center",
                            }}
                        >
                            Showing {filtered.length} of {leads.length} leads.
                        </p>
                    </div>
                </section>
    );
};

export default AdminLeadsPage;
